import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Checkbox,
    FormControlLabel,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../../context/AuthContext';
import notificationSubscriptionService from '../../services/notification-subscription.service';

const NotificationSubscriptionManager = () => {
    const { user } = useAuth();
    const [filters, setFilters] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedSubscriptions, setSelectedSubscriptions] = useState({});
    const [saving, setSaving] = useState(false);
    const [groupedFilters, setGroupedFilters] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            console.log('Fetching data, user:', user);
            if (!user?.user_id) {
                console.log('No user ID available, returning early');
                return;
            }
            
            try {
                setLoading(true);
                setError(null);
                
                console.log('Calling API for filters and subscriptions');
                const [filtersData, subscriptionsData] = await Promise.all([
                    notificationSubscriptionService.getFilters(),
                    notificationSubscriptionService.getUserSubscriptions(user.user_id)
                ]);
                
                console.log('API responses:', { filtersData, subscriptionsData });
                
                // Group filters by domain
                const grouped = {};
                filtersData.forEach(filter => {
                    if (!grouped[filter.domain]) {
                        grouped[filter.domain] = [];
                    }
                    grouped[filter.domain].push(filter);
                });
                
                setGroupedFilters(grouped);
                setFilters(filtersData);
                setSubscriptions(subscriptionsData);
                
                // Initialize selected subscriptions based on current subscriptions
                const initialSelected = {};
                filtersData.forEach(filter => {
                    initialSelected[filter.filter_id] = subscriptionsData.some(
                        sub => sub.filter_id === filter.filter_id && sub.is_active
                    );
                });
                setSelectedSubscriptions(initialSelected);
            } catch (err) {
                console.error('Error loading notification data:', err);
                setError('Failed to load notification settings. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user]);

    const handleSubscriptionToggle = (filterId) => {
        setSelectedSubscriptions(prev => ({
            ...prev,
            [filterId]: !prev[filterId]
        }));
    };

    const handleDomainToggle = (domain) => {
        const domainFilters = groupedFilters[domain] || [];
        const allSelected = domainFilters.every(filter => selectedSubscriptions[filter.filter_id]);
        
        const newSelected = { ...selectedSubscriptions };
        domainFilters.forEach(filter => {
            newSelected[filter.filter_id] = !allSelected;
        });
        
        setSelectedSubscriptions(newSelected);
    };

    const handleSaveSubscriptions = async () => {
        if (!user?.user_id) return;
        
        setSaving(true);
        setError(null);
        setSuccess(null);
        
        try {
            // Get current subscriptions
            const currentSubscriptions = await notificationSubscriptionService.getUserSubscriptions(user.user_id);
            const currentSubscriptionMap = {};
            
            currentSubscriptions.forEach(sub => {
                currentSubscriptionMap[sub.filter_id] = sub.is_active;
            });
            
            // Process changes
            const changes = [];
            
            // Check for subscriptions to add or activate
            Object.entries(selectedSubscriptions).forEach(([filterId, isSelected]) => {
                const isCurrentlyActive = currentSubscriptionMap[filterId] === true;
                
                if (isSelected && !isCurrentlyActive) {
                    changes.push(() => notificationSubscriptionService.subscribeToFilter(user.user_id, filterId));
                }
            });
            
            // Check for subscriptions to remove or deactivate
            Object.entries(selectedSubscriptions).forEach(([filterId, isSelected]) => {
                const isCurrentlyActive = currentSubscriptionMap[filterId] === true;
                
                if (!isSelected && isCurrentlyActive) {
                    changes.push(() => notificationSubscriptionService.unsubscribeFromFilter(user.user_id, filterId));
                }
            });
            
            // Execute all changes
            await Promise.all(changes.map(change => change()));
            
            // Refresh subscriptions
            const updatedSubscriptions = await notificationSubscriptionService.getUserSubscriptions(user.user_id);
            setSubscriptions(updatedSubscriptions);
            
            setSuccess('Notification preferences saved successfully');
        } catch (err) {
            console.error('Error saving subscriptions:', err);
            setError('Failed to save notification preferences. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                        Notification Settings
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveSubscriptions}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                    Manage which notifications you want to receive
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                {Object.entries(groupedFilters).map(([domain, domainFilters]) => (
                    <Accordion key={domain} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" width="100%">
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={domainFilters.every(filter => selectedSubscriptions[filter.filter_id])}
                                            indeterminate={
                                                domainFilters.some(filter => selectedSubscriptions[filter.filter_id]) && 
                                                !domainFilters.every(filter => selectedSubscriptions[filter.filter_id])
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDomainToggle(domain);
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="subtitle1">
                                            {domain}
                                        </Typography>
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <Chip 
                                    label={`${domainFilters.filter(f => selectedSubscriptions[f.filter_id]).length}/${domainFilters.length}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ ml: 2 }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Divider sx={{ mb: 2 }} />
                            {domainFilters.map((filter) => (
                                <Box 
                                    key={filter.filter_id} 
                                    display="flex" 
                                    alignItems="center" 
                                    justifyContent="space-between"
                                    py={1}
                                    borderBottom="1px solid #f0f0f0"
                                >
                                    <Box display="flex" alignItems="center" flex={1}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={selectedSubscriptions[filter.filter_id] || false}
                                                    onChange={() => handleSubscriptionToggle(filter.filter_id)}
                                                    disabled={!filter.is_active}
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body1">
                                                        {filter.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {filter.description}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Box>
                                    <Box>
                                        <Chip 
                                            label={filter.event_type} 
                                            size="small" 
                                            color="primary" 
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </CardContent>
        </Card>
    );
};

export default NotificationSubscriptionManager; 